import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { LogoMark } from "../components/LogoMark";

export const Hero = () => {
  return (
    <section
      id="home"
      className="relative min-h-[92svh] overflow-hidden bg-[#1f2935]"
      data-testid="hero-section"
    >
      {/* Background image (visible) */}
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_dn-interiors/artifacts/24qmbcdd_Content-Creator-Project%20%281%29.png"
          alt=""
          className="w-full h-full object-cover"
          aria-hidden="true"
        />
        {/* Subtle dark wash for legibility without killing the image */}
        <div className="absolute inset-0 bg-[#1f2935]/30" />
        {/* Darker at top (behind nav) and bottom (into page) */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1f2935]/50 via-transparent to-[#1f2935]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-24 pb-20 lg:pt-28 lg:pb-28 flex flex-col items-center text-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="flex flex-col items-center"
        >
          <LogoMark height={300} className="mb-6 sm:hidden" />
          <LogoMark height={440} className="hidden sm:block mb-8" />

          {/* Content card — gives text a focused, readable surface over the image */}
          <div className="relative w-full max-w-2xl mx-auto rounded-2xl bg-[#1f2935]/75 backdrop-blur-md border border-white/10 px-6 sm:px-10 py-10 sm:py-12 shadow-2xl shadow-black/40">
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="h-px w-8 bg-[#D4AF37]" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">
                Flooring & Interior Design
              </span>
              <span className="h-px w-8 bg-[#D4AF37]" />
            </div>

            <h1 className="font-serif tracking-tight text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
              Modern flooring.
              <br />
              <span className="text-[#D4AF37]">Done right.</span>
            </h1>

            <p className="mt-6 text-sm sm:text-[17px] text-slate-200 leading-[1.7] max-w-lg mx-auto">
              Flooring, cabinets, tile, paint, and full interior remodeling
              across Manatee, Sarasota, and Charlotte County, FL.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/quote"
                className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#D4AF37] text-[#1f2935] font-semibold text-[12px] uppercase tracking-[0.2em] hover:bg-[#B48600] transition-colors shadow-lg shadow-black/40"
                data-testid="hero-quote-btn"
              >
                Get a Quote
                <ArrowRight
                  size={14}
                  strokeWidth={2}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-white/25 text-white font-medium text-[12px] uppercase tracking-[0.2em] hover:border-[#D4AF37]/60 hover:text-[#D4AF37] hover:bg-white/5 transition-colors"
                data-testid="hero-work-btn"
              >
                View Services
              </Link>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Stats strip */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="relative z-10 border-t border-white/10 bg-[#1f2935]/80 backdrop-blur-sm"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 grid grid-cols-3 gap-4 sm:gap-8 text-center">
          {[
            { k: "10+", v: "Years of craft" },
            { k: "3", v: "Florida counties" },
            { k: "24h", v: "Quote turnaround" },
          ].map((s) => (
            <div key={s.v} className="flex flex-col items-center">
              <div className="font-serif text-2xl sm:text-3xl text-[#D4AF37] tracking-tight">
                {s.k}
              </div>
              <div className="mt-1 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-slate-400">
                {s.v}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
